import {
  FormDataTypes,
  FormErrors,
  FormTouched,
  InputTypes,
} from 'interfaces/form.interface';
import { FormEvent, useRef, useState } from 'react';
import { isNotEmptyObject } from 'utils/utilities';

const initialFormData: FormDataTypes = {
  companyEmail: '',
  companyName: '',
  firstName: '',
  honeypot: '',
  joinMailingList: false,
  lastName: '',
  message: '',
};

const REQUIRED_FIELDS: (keyof FormDataTypes)[] = [
  'firstName',
  'lastName',
  'companyName',
  'companyEmail',
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (data: FormDataTypes): FormErrors => {
  const errors: FormErrors = {};

  REQUIRED_FIELDS.forEach((field) => {
    if (!String(data[field]).trim()) {
      errors[field] = 'form_error_required';
    }
  });

  if (!errors.companyEmail && !EMAIL_REGEX.test(data.companyEmail.trim())) {
    errors.companyEmail = 'form_error_email';
  }

  return errors;
};

const useForm = (
  initialState: FormDataTypes = initialFormData,
  onValidated: any
) => {
  const [formData, setFormData] = useState(initialState);
  const [touched, setTouched] = useState<FormTouched>({});
  const openedAt = useRef(Date.now());

  const errors = validate(formData);

  const handleInputChange = ({ name, value, type, checked }: InputTypes) =>
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });

  const handleBlur = ({ name }: Pick<InputTypes, 'name'>) =>
    setTouched((prev) => ({ ...prev, [name]: true }));

  const handleReset = () => {
    setFormData(initialFormData);
    setTouched({});
    openedAt.current = Date.now();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isNotEmptyObject(errors)) {
      setTouched(
        REQUIRED_FIELDS.reduce(
          (acc, field) => ({ ...acc, [field]: true }),
          {} as FormTouched
        )
      );
      return;
    }

    onValidated({
      companyEmail: formData.companyEmail.trim(),
      companyName: formData.companyName.trim(),
      firstName: formData.firstName.trim(),
      joinMailingList: formData.joinMailingList,
      lastName: formData.lastName.trim(),
      message: formData.message.trim(),
      honeypot: formData.honeypot,
      elapsedMs: Date.now() - openedAt.current,
    });
  };

  return {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    handleReset,
    handleSubmit,
  };
};

export default useForm;
